import { useEffect, useRef } from 'react'

// The hardware around the landing page's screenshots and recordings: real
// product images with no brand on them (scripts/devices.sh writes them to
// public/devices). The phone is Android Studio's Pixel 9 Pro art (AOSP,
// Apache 2.0): the frame, the screen inside it, and the camera lens laid over
// the screen. The watch is the Connect IQ simulator's Venu 3 render, which
// carries no wordmark. A browser window, drawn, holds the desktop.

/** A real screenshot (make screenshots-phone), WebP with a PNG fallback. */
export function Shot({ name, width, height, alt = '', className, priority = false }: {
  name: string; width: number; height: number; alt?: string; className?: string
  /** Above the fold: load at once, first (it is the page's largest paint). */
  priority?: boolean
}) {
  return (
    <picture className={className}>
      <source srcSet={`/images/${name}.webp`} type="image/webp" />
      <img src={`/images/${name}.png`} width={width} height={height} alt={alt} decoding="async"
        loading={priority ? 'eager' : 'lazy'} {...(priority ? { fetchpriority: 'high' } : {})} />
    </picture>
  )
}

/**
 * A recording of the real app (scripts/clips.sh): muted, played only while it
 * is on screen and paused when it leaves, its poster the still before it plays.
 * Anyone who asked for less motion keeps the still. Clips in one `group` (a
 * phone and a watch filmed together) start, stop and start over as one.
 */
export function Clip({ name, width, height, group }: { name: string; width: number; height: number; group?: string }) {
  const ref = useRef<HTMLVideoElement>(null)
  useEffect(() => {
    const v = ref.current
    if (!v || typeof IntersectionObserver === 'undefined') return
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return
    v.muted = true
    const peers = () => (group
      ? [...document.querySelectorAll<HTMLVideoElement>(`video[data-clip-group="${group}"]`)]
      : [v])
    const io = new IntersectionObserver(([entry]) => {
      for (const p of peers()) {
        p.muted = true
        if (entry.isIntersecting) void p.play().catch(() => {})
        else p.pause()
      }
    }, { threshold: 0.4 })
    io.observe(v)
    // One group, one clock: when one ends, all start over from the top.
    const again = () => { for (const p of peers()) { p.currentTime = 0; void p.play().catch(() => {}) } }
    if (group) v.addEventListener('ended', again)
    return () => { io.disconnect(); v.removeEventListener('ended', again) }
  }, [group])
  return (
    <video ref={ref} className="dv-clip" width={width} height={height} poster={`/images/${name}.webp`}
      muted playsInline loop={!group} preload="none" aria-hidden="true" data-clip-group={group}>
      <source src={`/videos/${name}.webm`} type="video/webm" />
      <source src={`/videos/${name}.mp4`} type="video/mp4" />
    </video>
  )
}

/** A phone: a screenshot (`shot`), or a recording (`clip`) in its place. */
export function Phone({ shot, clip, group, priority = false, className = '' }: {
  shot: string; clip?: string; group?: string; priority?: boolean; className?: string
}) {
  return (
    <div className={`dv-phone ${className}`}>
      <img className="dv-phone__frame" src="/devices/phone.webp" width={704} height={1487} alt="" decoding="async" />
      <div className="dv-phone__screen">
        {clip ? <Clip name={clip} width={560} height={1212} group={group} /> : <Shot name={shot} width={560} height={1212} priority={priority} />}
        <img className="dv-phone__lens" src="/devices/phone-lens.webp" width={640} height={1428} alt="" decoding="async" />
      </div>
    </div>
  )
}

/** A watch: its round screen holds a screenshot (`shot`), or a recording (`clip`). */
export function Watch({ shot, clip, group, className = '' }: { shot: string; clip?: string; group?: string; className?: string }) {
  return (
    <div className={`dv-watch ${className}`}>
      <img className="dv-watch__body" src="/devices/watch.webp" width={636} height={882} alt="" decoding="async" />
      <div className="dv-watch__screen">
        {clip ? <Clip name={clip} width={390} height={390} group={group} /> : <Shot name={shot} width={390} height={390} />}
      </div>
    </div>
  )
}

export function Browser({ shot, alt, url = 'matlist.app' }: { shot: string; alt: string; url?: string }) {
  return (
    <figure className="dv-browser">
      <div className="dv-browser__bar" aria-hidden="true">
        <span className="dv-browser__dots"><i /><i /><i /></span>
        <span className="dv-browser__url">{url}</span>
      </div>
      <Shot name={shot} width={1440} height={900} alt={alt} />
    </figure>
  )
}
